import { useParams, useNavigate } from "react-router-dom";
import { JSX, useState } from "react";
import GridPhotoItem from "../../components/GridPhotoItem";
import { useIntl } from "react-intl";
import { useGetFindReport } from "../../hooks/useGetFindReport";
import { useGlobalData } from "../../context/GlobalContext";
import ImageGalleryCard from "../../components/ImageGalleryCard";
import { useAuth } from "../../context/AuthContext";
import { formatDateMMDDYYYY, formatShortDate } from "../../common/common.date";
import { AIRPORT, BRAND_COLOR, CUSTOMER_NOTIFICATION_STATUS_NOT_NOTIFIED, PLANE, stopWords } from "../../constants";
import { useGetLossReportsToMatchByReportId } from "../../hooks/useGetLossReportsToMatchByReportId";
import CompareNavigation from "../../components/CompareNavigation";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useMatchReports } from "../../hooks/useMatchReports";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { log } from "../../common/common.logger";
import { HighlightedTextProps } from "../../types";
import { useGetLossReportById } from "../../hooks/useGetLossReportById";
import { useSendFoundReportMailToClient } from "../../hooks/useSendFoundReportMailToClient";
import ReferenceNumberContainer from "../../components/ReferenceNumberContainer";
import { useGetReportResponses } from "../../hooks/useGetReportResponses";
import QuestionsAndResponses from "../../components/QuestionsAndResponses";

const Report = () => {
	const intl = useIntl();
	const queryClient = useQueryClient();

	const { findReportID } = useParams();
	const { userData } = useAuth();
	const navigate = useNavigate();

	const [extra, setExtra] = useState<string[]>([]);
	const [isCompareOn, setIsCompareOn] = useState(false);

	const {
		airlines,
		categories: allCategories,
		storageLocations,
		placeKindsList,
		placesList,
		statuses,
	} = useGlobalData();

	const [matchIndex, setMatchIndex] = useState(0);
	const [showConfirmMatch, setShowConfirmMatch] = useState(false);
	const [showConfirmSendNotification, setShowConfirmSendNotification] = useState(false);
	const [matchInProgress, setMatchInProgress] = useState(false);
	const [sendNotificationInProgress, setSendNotificationInProgress] = useState(false);

	const matchReportsMutation = useMatchReports();
	const useSendFoundReportMailToClientMutation = useSendFoundReportMailToClient();

	const { data: report, isLoading } = useGetFindReport({
		report: Number(findReportID),
	});

	const { data: lossReport } = useGetLossReportById({
		loserId: report?.Loser_fk,
	});

	const { data: responses } = useGetReportResponses({
		reportId: findReportID ? findReportID : "0",
		locale: userData?.user.locale || "en-US",
	});
 

	log("Report: ", { report, lossReport, responses });

	const { data: lossReportsToMatch } = useGetLossReportsToMatchByReportId({
		reportId: Number(findReportID),
	});

	const currentMatch = lossReportsToMatch?.[matchIndex];

		const { data: responsesHit } = useGetReportResponses({
		reportId: currentMatch ? currentMatch.Report_ID : "0",
		locale: userData?.user.locale || "en-US",
	});

	if (isLoading || !report) {
		return null;
	}

	const isMatched = report?.matchCount ? report.matchCount == -1 : false;

	const selectedCategory = allCategories?.find((c) => c.ItemCategory_ID == report.lossObjectCategoryFk);

	const rootCategory = allCategories?.find((c) => c.ItemCategory_ID == selectedCategory?.ItemCategory_fk);

	const selectedPlaceKind = placeKindsList?.find((a) => a.LossPlace_ID == report.findReportPlaceFk)?.LocalizedValue;

	const selectedPlace = placesList?.find((a) => a.LossPlace_ID == report.findReportChildPlaceFk)?.LocalizedValue;

	const selectedAirline = airlines?.find((a) => a.UID == report.findReportAirlineFk)?.AirlineName;

	const selectedStorageLocation = storageLocations?.find(
		(a) => a.StorageLocation_ID == report.lossObjectStorageLocationFk,
	)?.LocationName;

	const isPlane = report.findReportPlaceFk == PLANE;

	let lossReportData: {
		name: string | undefined;
		country: string | undefined;
		address: string | undefined;
		phone: string | undefined;
		mobilePhone: string | undefined;
		fax: string | undefined;
		email: string | undefined;
		status: string | undefined;
	} | null = null;

	if (lossReport) {
		lossReportData = {
			name: `${lossReport.FirstName} ${lossReport.LastName}`,
			country: lossReport.Country,
			address: `${lossReport.PostalCode} ${lossReport.Locality} ${lossReport.Street}`,
			phone: lossReport.Phone,
			mobilePhone: lossReport.MobilePhone,
			fax: lossReport.Fax,
			email: lossReport.Email,
			status: statuses?.find((s) => s.Status_ID === lossReport.Status_fk)?.StatusName,
		};
	}

	const handleAdopt = () => {
		setShowConfirmMatch(true);
	};

	const handleCancel = () => {
		setIsCompareOn(false);
	};

	const handleYesAdopt = async () => {
		log("Matching reports for: ", {
			F_ID: report.findReportID,
			L_ID: currentMatch?.Report_ID,
		});

		try {
			if (report.findReportID && currentMatch?.Report_ID) {
				setMatchInProgress(true);

				const matchResponse = await matchReportsMutation.mutateAsync({
					foundReportId: report.findReportID,
					lossReportId: currentMatch?.Report_ID,
				});

				log("Matching reports API response", matchResponse);

				setShowConfirmMatch(false);
				setIsCompareOn(false);

				setMatchInProgress(false);

				//@ts-ignore
				await queryClient.invalidateQueries(["getFindReports"]);

				navigate("/backoffice", { state: { refetch: Math.random() } });

				toast.success(intl.formatMessage({ id: "match_reports_success" }));
			} else {
				setShowConfirmMatch(false);
			}
		} catch (error) {
			toast.error(intl.formatMessage({ id: "match_reports_error" }));
			console.error("Error submitting match:", error);

			setMatchInProgress(false);
			setShowConfirmMatch(false);
		}
	};

	const handleCancelAdopt = () => {
		console.log("Cancelled");
		setShowConfirmMatch(false);
	};

	// Helper to clean and tokenize words
	const tokenize = (input: string): Set<string> => {
		return new Set(
			input
				.toLowerCase()
				.split(/[\s\/\-,().]+/) // split by spaces, slashes, hyphens, commas, etc.
				.map((word) => word.trim())
				.filter(Boolean),
		);
	};

	function getHighlightedText({ text, compareToText }: HighlightedTextProps): JSX.Element {
		if (!text || typeof text !== "string") return <>{text}</>;

		if (!compareToText || typeof compareToText !== "string" || compareToText.trim() === "") {
			return <>{text}</>;
		}

		const compareWords = tokenize(compareToText);

		// Split text into words and punctuation/padding
		const parts = text.split(/(\b[\w']+\b)/); // keeps words and boundaries

		return (
			<>
				{parts.map((part, index) => {
					const normalized = part.toLowerCase();
					const isStopWord = stopWords.has(normalized);
					const isMatch = compareWords.has(normalized);

					if (isMatch && !isStopWord) {
						return (
							<span key={index} style={{ backgroundColor: "lightgreen" }}>
								{part}
							</span>
						);
					}

					return <span key={index}>{part}</span>;
				})}
			</>
		);
	}

	const handleSendNotificationToCustomer = () => {
		setShowConfirmSendNotification(true);
	};

	const handleYesSendNotification = async () => {
		try {
			setSendNotificationInProgress(true);

			await useSendFoundReportMailToClientMutation.mutateAsync(report.findReportID);

			//@ts-ignore
			queryClient.invalidateQueries(["getLossReportById"]);

			setSendNotificationInProgress(false);
			setShowConfirmSendNotification(false);
		} catch (error) {
			setSendNotificationInProgress(false);
			setShowConfirmSendNotification(false);

			console.error("Error sending notification:", error);
			toast.error(intl.formatMessage({ id: "send_notification_error" }));
		}
	};

	const handleCancelSendNotification = () => {
		setShowConfirmSendNotification(false);
	};

	return (
		<div className="container mt-3">
			{isCompareOn && (
				<div className="sticky-top bg-white pt-2" style={{ zIndex: 1020 }}>
					<CompareNavigation
						onPrevious={() => setMatchIndex((i) => i - 1)}
						onNext={() => setMatchIndex((i) => i + 1)}
						onAdopt={handleAdopt}
						onCancel={handleCancel}
						disablePrevious={matchIndex === 0}
						disableNext={matchIndex >= (lossReportsToMatch?.length ?? 0) - 1}
					/>
				</div>
			)}

			{!isCompareOn ? (
				// Single View
				<>
					<div className="d-flex justify-content-between align-items-center mb-2">
						<button className="btn btn-light" onClick={() => navigate(-1)}>
							<i className="fas fa-arrow-left me-1"></i> {intl.formatMessage({ id: "back" })}
						</button>
						{!isMatched && lossReportsToMatch && lossReportsToMatch?.length > 0 ? (
							<button
								className="btn btn-light text-light"
								style={{ backgroundColor: BRAND_COLOR }}
								onClick={() => setIsCompareOn(true)}
							>
								<i className="fa-solid fa-code-compare text-light me-1"></i>{" "}
								{`Show (${lossReportsToMatch?.length}) matches`}
							</button>
						) : null}
						{isMatched ? (
							<div className="d-flex justify-content-end align-items-center gap-2">
								{lossReport?.Status_fk?.toLowerCase() == CUSTOMER_NOTIFICATION_STATUS_NOT_NOTIFIED ? (
									<button
										className="btn btn-warning text-dark"
										onClick={handleSendNotificationToCustomer}
									>
										<i className="fa-solid fa-envelope text-dark me-1 "></i>{" "}
										{intl.formatMessage({ id: "notify_customer" })}
									</button>
								) : (
									<div
										className="alert alert-success p-1 m-0 d-flex align-items-center  ps-2 pe-2"
										role="alert"
									>
										<i className="fa-solid fa-envelope fs-6 me-1"></i>
										{intl.formatMessage({ id: "notified" })}
									</div>
								)}
								<div className="alert alert-success p-1 m-0 d-flex align-items-center" role="alert">
									<i className="fa-solid fa-check fs-6 me-1"></i>
									{intl.formatMessage({ id: "matched" })}
								</div>
							</div>
						) : null}
					</div>
					<ReferenceNumberContainer referenceNumber={report.findReportReferenceNumber} />
					<ImageGalleryCard
						images={report.lossObjectImages}
						children={
							<div className="card-body p-0">
								<div className="mb-2 d-flex justify-content-between">
									<div className="text-muted fw-bold">{intl.formatMessage({ id: "category" })}</div>
									<div className="text-end">{selectedCategory?.LocalizedValue}</div>
								</div>
								<hr />
								<div className="mb-2 d-flex justify-content-between bg-light">
									<div className="text-muted fw-bold">
										{intl.formatMessage({ id: "airport_plane" })}
									</div>
									<div className="text-end">{selectedPlaceKind}</div>
								</div>

								{isPlane ? (
									<>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "airline" })}
											</div>
											<div className="text-end">{selectedAirline}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between bg-light">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "flight_number" })}
											</div>
											<div className="text-end">{report.findReportFlightNumber}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "flight_date" })}
											</div>
											<div className="text-end">
												{formatShortDate(report.findReportFlightDate, userData?.user.locale)}
											</div>
										</div>
									</>
								) : (
									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">{intl.formatMessage({ id: "place" })}</div>
										<div className="text-end">{selectedPlace}</div>
									</div>
								)}

								<div className="mb-2 d-flex justify-content-between bg-light">
									<div className="text-muted fw-bold">
										{intl.formatMessage({ id: "storage_location" })}
									</div>
									<div className="text-end">{selectedStorageLocation}</div>
								</div>

								<hr />
								<fieldset className="mb-3 border rounded p-2">
									<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
										{intl.formatMessage({ id: "public_description" })}
									</legend>
									<div>{report.lossObjectPublicDescription}</div>
								</fieldset>
								<fieldset className="mb-3 border rounded p-2">
									<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
										{intl.formatMessage({ id: "internal_description" })}
									</legend>
									<div>{report.lossObjectInternalDescription}</div>
								</fieldset>
								<fieldset className="mb-3 border rounded p-2">
									<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
										{intl.formatMessage({ id: "questions_and_responses" })}
									</legend>
									<div>
										<QuestionsAndResponses responses={responses} />
									</div>
								</fieldset>
								{isMatched && lossReportData ? (
									<div>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												<h5>{intl.formatMessage({ id: "customer" })}</h5>
											</div>
											<div className="text-end">{""}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between align-items-center">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "status" })}
											</div>
											<div
												className={`badge bg-${
													lossReport?.Status_fk?.toLowerCase() ==
													CUSTOMER_NOTIFICATION_STATUS_NOT_NOTIFIED
														? "danger"
														: "success"
												} d-flex align-items-center`}
											>
												{lossReportData.status}
											</div>
										</div>
										<hr />
										<div className="mb-2 d-flex justify-content-between bg-light">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "name" })}
											</div>
											<div className="text-end">{lossReportData.name}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "country" })}
											</div>
											<div className="text-end">{lossReportData.country}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between bg-light">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "address" })}
											</div>
											<div className="text-end">{lossReportData.address}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "phone" })}
											</div>
											<div className="text-end">
												{lossReportData.phone ? lossReportData.phone : "-"}
											</div>
										</div>
										<div className="mb-2 d-flex justify-content-between bg-light">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "mobile_phone" })}
											</div>
											<div className="text-end">{lossReportData.mobilePhone}</div>
										</div>
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "email" })}
											</div>
											<div className="text-end">{lossReportData.email}</div>
										</div>
									</div>
								) : null}
							</div>
						}
					/>

					<div className="row p-2">
						{extra.map((src, i) => (
							<GridPhotoItem key={i} src={src} noAction={true} />
						))}
					</div>
				</>
			) : (
				// Compare View
				<div className="row">
					{/* Left Side */}
					<div className="col-md-6">
						{!isCompareOn ? (
							<div className="d-flex justify-content-between align-items-center mb-2">
								<button className="btn btn-light" onClick={() => navigate(-1)}>
									<i className="fas fa-arrow-left me-1"></i> {intl.formatMessage({ id: "back" })}
								</button>
							</div>
						) : null}
						<ReferenceNumberContainer referenceNumber={report.findReportReferenceNumber} />
						<ImageGalleryCard
							images={report.lossObjectImages}
							children={
								<div className="card-body p-0">
									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "category" })}
										</div>
										<div className="text-end">{selectedCategory?.LocalizedValue}</div>
									</div>
									<hr />
									<fieldset
										style={{
											height: 150,
											overflowY: "auto",
											backgroundColor: "#fff6d9",
										}}
										className="mb-3 border rounded p-2"
									>
										<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
											{intl.formatMessage({ id: "public_description" })}
										</legend>
										<div>
											{getHighlightedText({
												text: report.lossObjectPublicDescription,
												compareToText: currentMatch?.PublicDescription,
											})}
										</div>
									</fieldset>
									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "airport_plane" })}
										</div>
										<div className="text-end">{selectedPlaceKind}</div>
									</div>

									{isPlane ? (
										<>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "airline" })}
												</div>
												<div className="text-end">{selectedAirline}</div>
											</div>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "flight_number" })}
												</div>
												<div className="text-end">{report.findReportFlightNumber}</div>
											</div>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "flight_date" })}
												</div>
												<div className="text-end">
													{formatShortDate(
														report.findReportFlightDate,
														userData?.user.locale,
													)}
												</div>
											</div>
										</>
									) : (
										<div className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "place" })}
											</div>
											<div className="text-end">{selectedPlace}</div>
										</div>
									)}

									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "storage_location" })}
										</div>
										<div className="text-end">{selectedStorageLocation}</div>
									</div>

									<hr />

									<fieldset className="mb-3 border rounded p-2">
										<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
											{intl.formatMessage({ id: "internal_description" })}
										</legend>
										<div>{report.lossObjectInternalDescription}</div>
									</fieldset>
									<fieldset className="mb-3 border rounded p-2">
										<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
											{intl.formatMessage({ id: "questions_and_responses" })}
										</legend>
										<QuestionsAndResponses responses={responses} />
									</fieldset>
								</div>
							}
						/>

						<div className="row p-2">
							{extra.map((src, i) => (
								<GridPhotoItem key={i} src={src} noAction={true} />
							))}
						</div>
					</div>

					{/* Right Side */}
					<div className="col-md-6">
						<ReferenceNumberContainer referenceNumber={currentMatch ? currentMatch?.ReferenceNumber : ""} />
						<ImageGalleryCard
							images={
								currentMatch && currentMatch?.lossObjectImages
									? JSON.parse(currentMatch.lossObjectImages)
									: []
							}
							children={
								<div className="card-body p-0">
									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "category" })}
										</div>
										<div className="text-end">
											{currentMatch?.ItemCategory_fk
												? allCategories?.find(
														(c) => c.ItemCategory_ID == currentMatch?.ItemCategory_fk,
													)?.LocalizedValue
												: null}
										</div>
									</div>

									<hr />
									<fieldset
										style={{
											height: 150,
											overflowY: "auto",
											backgroundColor: "#fff3f7",
										}}
										className="mb-3 border rounded p-2"
									>
										<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
											{intl.formatMessage({ id: "public_description" })}
										</legend>
										<div>
											{getHighlightedText({
												text: currentMatch?.CustomerPublicDescription || "",
												compareToText: report.lossObjectPublicDescription,
											})}
										</div>
									</fieldset>
									<div className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "airport_plane" })}
										</div>
										<div className="text-end">
											{
												placeKindsList?.find(
													(a) => a.LossPlace_ID == currentMatch?.LossPlaceKind,
												)?.LocalizedValue
											}
										</div>
									</div>

									{currentMatch?.LossPlaceKind == AIRPORT ? (
										<div key={"location-airport"} className="mb-2 d-flex justify-content-between">
											<div className="text-muted fw-bold">
												{intl.formatMessage({ id: "location" })}
											</div>
											<div className="text-end">
												{
													placesList?.find(
														(a) => a.LossPlace_ID == currentMatch?.LossPlace_fk,
													)?.LocalizedValue
												}
											</div>
										</div>
									) : (
										<>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "airline" })}
												</div>
												<div className="text-end">
													{
														airlines?.find((a) => a.UID == currentMatch?.Airline_fk)
															?.AirlineName
													}
												</div>
											</div>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "flight_number" })}
												</div>
												<div className="text-end">{currentMatch?.FlightNumber}</div>
											</div>
											<div className="mb-2 d-flex justify-content-between">
												<div className="text-muted fw-bold">
													{intl.formatMessage({ id: "flight_date" })}
												</div>
												<div className="text-end">
													{currentMatch?.FlightDate
														? formatShortDate(
																currentMatch.FlightDate,
																userData?.user.locale,
															)
														: "—"}
												</div>
											</div>
										</>
									)}
									<div key={"recordDate"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "record_date" })}
										</div>
										<div className="text-end">
											{currentMatch ? formatDateMMDDYYYY(currentMatch?.RecordDate) : "—"}
										</div>
									</div>
									<div key={"expiryDate"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "expiry_date" })}
										</div>
										<div className="text-end">
											{currentMatch ? formatDateMMDDYYYY(currentMatch?.ExpiryDate) : "—"}
										</div>
									</div>
									<div key={"lossDate"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "loss_date" })}
										</div>
										<div className="text-end">
											{currentMatch ? formatDateMMDDYYYY(currentMatch?.LossDate) : "—"}
										</div>
									</div>

									<div key={"isPremiumReport"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "is_premium_report" })}
										</div>
										<div className="text-end">
											{currentMatch?.IsPremiumReport == true ? (
												<span>{intl.formatMessage({ id: "yes" })}</span>
											) : (
												<span>{intl.formatMessage({ id: "no" })}</span>
											)}
										</div>
									</div>
									<hr />
									<h4>{intl.formatMessage({ id: "customer" })}</h4>
									<div key={"name"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">{intl.formatMessage({ id: "name" })}</div>
										<div className="text-end">{currentMatch?.Name}</div>
									</div>
									<div key={"address"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "address" })}
										</div>
										<div className="text-end">{currentMatch?.Address}</div>
									</div>
									<div key={"phone"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">{intl.formatMessage({ id: "phone" })}</div>
										<div className="text-end">{currentMatch?.Phone}</div>
									</div>
									<div key={"mobilePhone"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">
											{intl.formatMessage({ id: "mobile_phone" })}
										</div>
										<div className="text-end">{currentMatch?.MobilePhone}</div>
									</div>
									<div key={"email"} className="mb-2 d-flex justify-content-between">
										<div className="text-muted fw-bold">{intl.formatMessage({ id: "email" })}</div>
										<div className="text-end">{currentMatch?.Email}</div>
									</div>
									<hr />
									{currentMatch?.Comment && currentMatch?.Comment.length > 0 ? (
										<fieldset className="mb-3 border rounded p-2">
											<legend className="float-none w-auto px-2 text-muted fw-bold mb-1 small">
												{intl.formatMessage({ id: "comment" })}
											</legend>
											<div>{currentMatch?.Comment}</div>
										</fieldset>
									) : null}
									<QuestionsAndResponses responses={responsesHit} />
								</div>
							}
						/>

						<div className="row p-2">
							{extra.map((src, i) => (
								<GridPhotoItem key={i} src={src} noAction={true} />
							))}
						</div>
					</div>
				</div>
			)}
			<ConfirmDialog
				show={showConfirmMatch}
				title={intl.formatMessage({ id: "confirm_adopt" })}
				message={intl.formatMessage({ id: "confirm_adopt_message" })}
				loadingMessage={intl.formatMessage({ id: "matching_in_progress" })}
				onYes={handleYesAdopt}
				onCancel={handleCancelAdopt}
				loadingState={matchInProgress}
			/>
			<ConfirmDialog
				show={showConfirmSendNotification}
				title={intl.formatMessage({ id: "confirm_send_notification" })}
				message={intl.formatMessage({
					id: "confirm_send_notification_message",
				})}
				loadingMessage={intl.formatMessage({
					id: "sending_notification_in_progress",
				})}
				onYes={handleYesSendNotification}
				onCancel={handleCancelSendNotification}
				loadingState={sendNotificationInProgress}
			/>
		</div>
	);
};

export default Report;
